
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  maxTocLevel: 4,
  layout: 'guide',
  section: 'Signal',
  title: 'Signal Rules',
  description: 'Learn about configuring Signal Rules'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title={metadata.title} subtitle={metadata.description} mdxType="Header" />
    <h4 {...{
      "id": "using-signal-rules"
    }}>{`Using Signal Rules`}</h4>
    <p>{`With Signal Rules, you can configure your decision logic directly within the Signal Dashboard using templates from Plaid, then use Dashboard tools to tailor the rules to your own traffic. `}</p>
    <p>{`Signal Rules are available to users of both Signal Transaction Scores and Balance products. `}</p>
    <p>{`Rules can be based on scores (higher scores indicate higher risk), attributes of the transaction, or a combination.`}</p>
    <p>{`You can set up the following actions for your business to take as a result of a rule:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`ACCEPT`}</inlineCode>{` and process the transaction`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`REVIEW`}</inlineCode>{` the transaction further before processing`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`REROUTE`}</inlineCode>{` the customer to another payment method, as the transaction is too risky`}</p>
      </li>
    </ul>
    <p>{`You can also create more complex action flows through the use of `}<a parentName="p" {...{
        "href": "#using-a-custom-action-key-for-advanced-actions"
      }}>{`custom action keys`}</a>{`.`}</p>
    <p>{`You can define a single ruleset for all your transactions, or you can build use-case specific rulesets, such as one for an initial deposit during customer onboarding and another for a returning customer depositing funds. `}</p>
    <h4 {...{
      "id": "creating-a-ruleset"
    }}>{`Creating a ruleset`}</h4>
    <p>{`Before using Signal Rules, you must create a Ruleset in the Dashboard, under `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/signal/risk-profiles"
      }}><strong parentName="a">{`Signal > Rules`}</strong></a>{`. You can create a ruleset from scratch, or Plaid can suggest an initial ruleset template relevant for your use case by utilizing context collected during your onboarding (such as transaction size). `}</p>
    <p>{`Rules are evaluated sequentially, from top to bottom, typically ordered by the severity of the rule violation. If data that the rule depends on is not available, the rule will be skipped. `}</p>
    <p>{`The `}<inlineCode parentName="p">{`result`}</inlineCode>{` of the first rule that matches will be applied to the transaction and included in the `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` response. You will be required to have a fallback rule that is applied if no other rules match.`}</p>
    <Image src='/assets/img/docs/signal/ruleset-example.png' alt='Example Plaid Signal Ruleset, showing each rule&apos;s condition, result, and the number of transactions and rate it affects' caption='Signal Ruleset example configuration' expandable={true} mdxType="Image" />
    <h4 {...{
      "id": "signal-transaction-score-versus-balance-only-rulesets"
    }}>{`Signal Transaction Score versus Balance-only rulesets`}</h4>
    <p>{`When creating a ruleset, you will be given the choice between creating a `}<strong parentName="p">{`Signal Transaction Score`}</strong>{` or `}<strong parentName="p">{`Balance-only`}</strong>{` ruleset.`}</p>
    <Image src='/assets/img/docs/signal/create-new-ruleset.png' expandable={true} mdxType="Image" />
    <Callout mdxType="Callout">
      <p>{`Balance customers who are not enabled for Signal Transaction Scores can create only `}<strong parentName="p">{`Balance-only`}</strong>{` rulesets.`}</p>
    </Callout>
    <p><inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` calls that use a `}<strong parentName="p">{`Signal Transaction Score ruleset`}</strong>{`:`}</p>
    <ul>
      <li parentName="ul">{`Return the full `}<inlineCode parentName="li">{`core_attributes`}</inlineCode>{` array with all 80+ core attributes, all of which can be used in the rule configuration`}</li>
      <li parentName="ul">{`Have ultra-low latency (p95 <2s) `}</li>
      <li parentName="ul">{`May use cached data`}</li>
      <li parentName="ul">{`Will not perform a real-time balance extraction`}</li>
      <li parentName="ul">{`Will be billed as Signal Transaction Score calls`}</li>
      <li parentName="ul">{`Are available only to Signal Transaction Scores customers`}</li>
    </ul>
    <p><inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` calls that use a `}<strong parentName="p">{`Balance-only ruleset`}</strong>{`:`}</p>
    <ul>
      <li parentName="ul">{`Return only two `}<inlineCode parentName="li">{`core_attributes`}</inlineCode>{`: `}<inlineCode parentName="li">{`available_balance`}</inlineCode>{` and `}<inlineCode parentName="li">{`current_balance`}</inlineCode>{`, which can be used in the rule configuration`}</li>
      <li parentName="ul">{`Have higher latency (p95 ~11s)`}</li>
      <li parentName="ul">{`Always perform a real-time fresh balance extraction`}</li>
      <li parentName="ul">{`Will be billed as Balance calls`}</li>
      <li parentName="ul">{`Are available to all customers on the Plaid Signal platform, including Balance-only customers`}</li>
    </ul>
    <h4 {...{
      "id": "using-signal-ruleset-results"
    }}>{`Using Signal Ruleset results`}</h4>
    <p>{`To evaluate a transaction's risk, call `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` and include the required request properties in addition to the ruleset's `}<inlineCode parentName="p">{`ruleset_key`}</inlineCode>{`, which can be found in the Dashboard, in smaller type under the name of the ruleset. `}</p>
    <p>{`The more properties you provide when calling `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{`, such as the `}<inlineCode parentName="p">{`user`}</inlineCode>{` or `}<inlineCode parentName="p">{`device`}</inlineCode>{` fields, the more accurate the results will be. Providing the `}<inlineCode parentName="p">{`client_user_id`}</inlineCode>{` is also recommended to aid in your debugging process later.`}</p>
    <p>{`In the response, use the `}<inlineCode parentName="p">{`ruleset.result`}</inlineCode>{` property to determine the next steps.`}</p>
    <p>{`Most integrations use the `}<inlineCode parentName="p">{`result`}</inlineCode>{` to decide between approving a payment for processing (`}<inlineCode parentName="p">{`result=ACCEPT`}</inlineCode>{`), or rerouting the consumer to another payment method (`}<inlineCode parentName="p">{`result=REROUTE`}</inlineCode>{`). If that's the case with your integration, ensure that your ruleset emits only these two result types.`}</p>
    <p>{`If your integration is taking more complex actions before deciding whether to process the transaction (for example, sending certain transactions to a manual review queue), use the `}<inlineCode parentName="p">{`REVIEW`}</inlineCode>{` result type. Remember to `}<a parentName="p" {...{
        "href": "/docs/signal/reporting-returns/#reporting-decisions"
      }}>{`report the final decision`}</a>{` for these reviewed transactions later.`}</p>
    <Callout warning mdxType="Callout">
      <p>{`Signal Transaction Score results can never be used as an input to determine a consumer's eligibility to access your services. Signal Transaction Scores evaluates the risk of a transaction from a specific bank account and can be used to determine if you should require the user to use a different payment method, according to your risk tolerance.`}</p>
    </Callout>
    <h4 {...{
      "id": "opting-out-of-the-default-ruleset"
    }}>{`Opting out of the default ruleset`}</h4>
    <p>{`If you do not specify a `}<inlineCode parentName="p">{`ruleset_key`}</inlineCode>{`, your `}<inlineCode parentName="p">{`default`}</inlineCode>{` ruleset will be used. If you do not have a `}<inlineCode parentName="p">{`default`}</inlineCode>{` ruleset and the `}<inlineCode parentName="p">{`ruleset_key`}</inlineCode>{` is not specified, `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` will return a `}<a parentName="p" {...{
        "href": "/docs/errors/signal/#ruleset_not_found"
      }}><inlineCode parentName="a">{`RULESET_NOT_FOUND`}</inlineCode></a>{` error unless you have opted out of the default ruleset. If you omit the `}<inlineCode parentName="p">{`ruleset_key`}</inlineCode>{` and have opted out of the default ruleset, `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` will return `}<inlineCode parentName="p">{`core_attributes`}</inlineCode>{` but will not return a `}<inlineCode parentName="p">{`ruleset`}</inlineCode>{` or a `}<inlineCode parentName="p">{`ruleset.result`}</inlineCode>{`.`}</p>
    <p>{`All customers who were enabled for Signal Transaction Scores in Production before October 15, 2025, are automatically opted out of the default ruleset, in order to maintain parity with earlier behavior (before ruleset functionality was introduced). `}</p>
    <p>{`Signal Transaction Scores customers who use `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` exclusively to incorporate raw insights from the `}<inlineCode parentName="p">{`core_attributes`}</inlineCode>{` into their own risk models, and do not want to use Signal rulesets at all, can choose to opt out of the default ruleset by contacting their account manager. For more details on using Signal Transaction Scores in this mode, see `}<a parentName="p" {...{
        "href": "#accessing-scores-and-attributes-directly-via-api"
      }}>{`Accessing scores and attributes directly via API`}</a>{`.`}</p>
    <p>{`Opting out of the default ruleset only impacts behavior when no ruleset is specified in the `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` call; if you explicitly specify a ruleset when calling `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{`, the ruleset will be used.`}</p>
    <p>{`Balance-only customers cannot opt out of the default ruleset. If you wish to check real-time balance without a ruleset, use `}<inlineCode parentName="p">{`/accounts/balance/get`}</inlineCode>{` instead of `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{`. `}</p>
    <p>{`To re-enable using default rulesets, contact your account manager. `}</p>
    <h5 {...{
      "id": "for-existing-customers-prior-to-may-2025-migrating-away-from-the-outcome-field"
    }}>{`For existing customers prior to May 2025: Migrating away from the outcome field`}</h5>
    <p>{`Customers who used Signal Rules before May 2025 used the `}<inlineCode parentName="p">{`ruleset.outcome`}</inlineCode>{` field. This field has been replaced by `}<a parentName="p" {...{
        "href": "/docs/api/products/signal/#signal-evaluate-response-ruleset-result"
      }}><inlineCode parentName="a">{`ruleset.result`}</inlineCode></a>{`. Update your integration to use the new values from the `}<inlineCode parentName="p">{`ruleset.result`}</inlineCode>{` field:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Outcome (old)`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Result (new)`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`block`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`REROUTE`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`accept`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`ACCEPT`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`review`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`REVIEW`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <p>{`For most integrations, that's all you need to change. Once you've updated your integration, inform your account manager.`}</p>
    <p>{`If you have an advanced use case where you take different actions for transactions with the same `}<inlineCode parentName="p">{`result`}</inlineCode>{`, you can use the `}<inlineCode parentName="p">{`custom_action_key`}</inlineCode>{`. For example, to apply a variable hold time, make sure your Signal rules emit a `}<inlineCode parentName="p">{`result=ACCEPT`}</inlineCode>{` value for all transactions you intend to process. Then, use the `}<inlineCode parentName="p">{`custom_action_key`}</inlineCode>{` to define a `}<inlineCode parentName="p">{`3-day-hold`}</inlineCode>{` for some rules and `}<inlineCode parentName="p">{`5-day-hold`}</inlineCode>{` for others. For more details, see `}<a parentName="p" {...{
        "href": "#using-a-custom-action-key-for-advanced-actions"
      }}>{`Using a custom action key`}</a>{`.`}</p>
    <h4 {...{
      "id": "using-a-custom-action-key-for-advanced-actions"
    }}>{`Using a custom action key for advanced actions`}</h4>
    <p>{`Some integrations may need more options for handling a transaction than the three choices of `}<inlineCode parentName="p">{`ACCEPT`}</inlineCode>{`, `}<inlineCode parentName="p">{`REVIEW`}</inlineCode>{`, or `}<inlineCode parentName="p">{`REROUTE`}</inlineCode>{`. Common use cases include:`}</p>
    <ul>
      <li parentName="ul">{`Applying a variable hold time to transactions that were accepted and processed, depending on the level of risk. `}</li>
      <li parentName="ul">{`Applying labeling to transactions to denote a cohort for data analysis purposes. `}</li>
      <li parentName="ul">{`Running a Signal Transaction Score ruleset check on a proposed transaction, and falling back to a higher-latency, Balance-only ruleset check if the transaction is higher risk.`}</li>
    </ul>
    <p>{`To support more complex actions, check the "Configure advanced options" box on the ruleset editing screen and specify a `}<inlineCode parentName="p">{`custom_action_key`}</inlineCode>{`.`}</p>
    <p>{`You can then create multiple rules with the same result but different custom actions. For example:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`If medium risk, `}<inlineCode parentName="p">{`ACCEPT`}</inlineCode>{` with `}<inlineCode parentName="p">{`custom_action_key=5-day-hold`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`If low risk, `}<inlineCode parentName="p">{`ACCEPT`}</inlineCode>{` with `}<inlineCode parentName="p">{`custom_action_key=3-day-hold`}</inlineCode></p>
      </li>
    </ul>
    <p>{`When calling `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{`, use the `}<a parentName="p" {...{
        "href": "/docs/api/products/signal/#signal-evaluate-response-ruleset-triggered-rule-details-custom-action-key"
      }}><inlineCode parentName="a">{`custom_action_key`}</inlineCode></a>{` in the response to determine the action to take.`}</p>
    <h4 {...{
      "id": "data-availability-limitations"
    }}>{`Data availability limitations`}</h4>
    <p>{`The amount of consumer-permissioned data available can vary. Plaid attempts to prefetch this data at a regular cadence and will also attempt to fetch fresh data during the `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` call if needed. Most Signal Transaction Score evaluations will return information about the Item's use in the network, the latest balance, historical balance information, or account activity. The full set of data is usually available to Plaid within 15 seconds of Plaid Link being completed. However, partial data may be returned if `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` is called before this process completes, the Item has been inactive for a long time, or the financial institution's connectivity to Plaid is degraded or down. Plaid may pause background refreshes for Items that have been inactive for an extended period. If this occurs, the next `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` call may take longer as Plaid attempts to fetch fresh data before returning a response.`}</p>
    <p>{`When used with Items that do not have a connection to a financial institution (i.e., Items created or verified using `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/"
      }}>{`alternative methods`}</a>{` such as micro-deposits or Database Auth), Plaid can generate Signal Transaction Scores for approximately 30% of these Items, although Items created in this way will have no data in the `}<inlineCode parentName="p">{`core_attributes`}</inlineCode>{` response property. If there is insufficient data, the `}<inlineCode parentName="p">{`scores`}</inlineCode>{` property will be `}<inlineCode parentName="p">{`null`}</inlineCode>{`, and any rules using `}<inlineCode parentName="p">{`score`}</inlineCode>{` will be skipped (potentially triggering your fallback rule). You will still be billed for the evaluation (unless you have no Signal Ruleset enabled).`}</p>
    <h4 {...{
      "id": "testing-signal-rules-in-sandbox"
    }}>{`Testing Signal Rules in Sandbox`}</h4>
    <p>{`In the Sandbox environment, scores and attributes returned by `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` will be randomly generated. You can influence the score returned by `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` in Sandbox by providing particular values in the `}<inlineCode parentName="p">{`amount`}</inlineCode>{` request field. Use the values from the following table to control the returned score:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Amount`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Score`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`3.53`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`10`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`12.17`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`60`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`27.53`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`90`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`You can use these hardcoded responses and create simple rules in Sandbox (such as `}<inlineCode parentName="p">{`REROUTE if bank_score == 90`}</inlineCode>{`) to test various result types. `}</p>
    <h4 {...{
      "id": "debugging-transactions"
    }}>{`Debugging transactions`}</h4>
    <p>{`All evaluations can be viewed in real time on the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/signal/transactions"
      }}>{`transactions viewer`}</a>{` Dashboard page. Look up your transaction using its `}<inlineCode parentName="p">{`client_transaction_id`}</inlineCode>{`. You can see its scores, the ruleset's `}<inlineCode parentName="p">{`result`}</inlineCode>{`, and the data used to build the score.`}</p>
    <h4 {...{
      "id": "debugging-rules"
    }}>{`Debugging rules`}</h4>
    <p>{`You can view all transactions that matched a specific rule by clicking `}<inlineCode parentName="p">{`View Performance`}</inlineCode>{` in the `}<inlineCode parentName="p">{`...`}</inlineCode>{` icon on the rule.`}</p>
    <Image src='/assets/img/docs/signal/view-rule-performance-nav.png' alt='Navigation to the rule performance page' caption='Navigation to the rule performance page' expandable={true} mdxType="Image" />
    <Image src='/assets/img/docs/signal/one-rule-performance.png' alt='Example performance page for a single Signal rule' caption='Performance page of a single rule' expandable={true} mdxType="Image" />
    <h4 {...{
      "id": "tuning-your-rules"
    }}>{`Tuning your rules`}</h4>
    <p>{`After your integration is live, Plaid will report metrics such as approval rate, and ACH return rate, in the Signal Dashboard. You will use these Dashboard tools to `}<a parentName="p" {...{
        "href": "/docs/signal/tuning-rules/"
      }}>{`tune your rule logic`}</a>{`, which is a critical step in a successful launch.`}</p>
    <Callout mdxType="Callout">
      <p>{`You must integrate the `}<inlineCode parentName="p">{`/signal/return/report`}</inlineCode>{` endpoint to use the Plaid Dashboard performance data features. If you do not report returns, performance data will not be available.`}</p>
    </Callout>
    <h4 {...{
      "id": "shadow-tests-and-proof-of-concept-testing"
    }}>{`Shadow tests and proof of concept testing`}</h4>
    <p>{`If you're using Signal as part of a shadow test or proof of concept, you should still follow all of the above integration guidance as though you were launching to production (including use of Signal Rules) - you just won't use the results to make the real-world payment decision. Log any relevant rule results, scores, or attributes as necessary.`}</p>
    <p>{`It is particularly critical to `}<a parentName="p" {...{
        "href": "/docs/signal/reporting-returns/"
      }}>{`report decisions and returns`}</a>{` in shadows and proof of concepts, in order for Plaid to measure performance and provide optimized rule suggestions tailored to your traffic.`}</p>
    <h4 {...{
      "id": "accessing-scores-and-attributes-directly-via-api"
    }}>{`Accessing scores and attributes directly via API`}</h4>
    <p>{`Plaid strongly recommends using Signal Rules. However, if you have your own risk management system in which you are using Signal Transaction Scores as one of multiple data sources, you might choose to ingest this data directly via the API instead. You can also ingest this data directly via the API while using Signal Rules as well. If you don't use Signal Rules, Plaid may be limited in the support it can offer you in tuning external logic.`}</p>
    <p>{`To directly access scores and data attributes, call `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{`. The `}<inlineCode parentName="p">{`scores`}</inlineCode>{` and `}<inlineCode parentName="p">{`core_attributes`}</inlineCode>{` properties in the response will contain the raw data you can provide to your model. For a full listing of `}<inlineCode parentName="p">{`core_attributes`}</inlineCode>{`, contact your account manager. If you are not using Signal Rules, you should also contact your account manager to `}<a parentName="p" {...{
        "href": "#opting-out-of-the-default-ruleset"
      }}>{`opt out of the default ruleset`}</a>{`.`}</p>
    <p>{`Even if you aren't using Signal Rules, it's still recommended to `}<a parentName="p" {...{
        "href": "/docs/signal/reporting-returns"
      }}>{`report decisions and returns`}</a>{` in order to use the `}<a parentName="p" {...{
        "href": "#tuning-your-rules"
      }}>{`Signal Performance pane`}</a>{`, which helps you understand performance and find the right score threshold.`}</p>
    <h4 {...{
      "id": "for-reseller-partners"
    }}>{`For reseller partners`}</h4>
    <p>{`A reseller partner will have a global ruleset that all their end customers inherit. If the reseller makes a change to that global default ruleset, all the end-customers also automatically adopt that change as well. End customers can also create their own rulesets, which will not propagate back to the reseller partner. `}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "using-signal-rules",
        level: 4,
        title: "Using Signal Rules",
      },
{
        id: "creating-a-ruleset",
        level: 4,
        title: "Creating a ruleset",
      },
{
        id: "signal-transaction-score-versus-balance-only-rulesets",
        level: 4,
        title: "Signal Transaction Score versus Balance-only rulesets",
      },
{
        id: "using-signal-ruleset-results",
        level: 4,
        title: "Using Signal Ruleset results",
      },
{
        id: "opting-out-of-the-default-ruleset",
        level: 4,
        title: "Opting out of the default ruleset",
      },
{
        id: "using-a-custom-action-key-for-advanced-actions",
        level: 4,
        title: "Using a custom action key for advanced actions",
      },
{
        id: "data-availability-limitations",
        level: 4,
        title: "Data availability limitations",
      },
{
        id: "testing-signal-rules-in-sandbox",
        level: 4,
        title: "Testing Signal Rules in Sandbox",
      },
{
        id: "debugging-transactions",
        level: 4,
        title: "Debugging transactions",
      },
{
        id: "debugging-rules",
        level: 4,
        title: "Debugging rules",
      },
{
        id: "tuning-your-rules",
        level: 4,
        title: "Tuning your rules",
      },
{
        id: "shadow-tests-and-proof-of-concept-testing",
        level: 4,
        title: "Shadow tests and proof of concept testing",
      },
{
        id: "accessing-scores-and-attributes-directly-via-api",
        level: 4,
        title: "Accessing scores and attributes directly via API",
      },
{
        id: "for-reseller-partners",
        level: 4,
        title: "For reseller partners",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
